<template>
    <main class="parts-history-page">
        <v-container class="parts-history-page__container">
            <account-settings-header
                :items="links"
                title="Parts order history"
                subtitle="Review parts you've ordered, delivery details, and shipment tracking."
                :icon="mdiPackageVariantClosed"
            />

            <header class="parts-history-page__heading">
                <div class="parts-history-page__heading-copy">
                    <span class="parts-history-page__eyebrow">YOUR ACCOUNT / ORDERS</span>
                    <h2>Order history</h2>
                    <p>Filter and review your parts orders, delivery addresses, and shipment dates.</p>
                </div>
                <div class="parts-history-page__heading-actions">
                    <span v-if="serverItemsLength !== null && typeof serverItemsLength !== 'undefined'" class="parts-history-page__count">
                        {{ serverItemsLength }} orders
                    </span>
                    <download-file-xlsx
                        class="parts-history-page__download"
                        :text="btn"
                        :table-array="downloadTableData"
                        :table-header="downloadTableHeader"
                        :file-name="filename"
                        :data-func="downloadData"
                        color="primary"
                        loader
                    />
                </div>
            </header>

            <section class="parts-history-card">
                <section class="parts-history-filters" aria-label="Filter parts orders">
                    <v-select
                        v-model="year"
                        class="parts-history-filter"
                        label="Year"
                        :items="years"
                        dark
                        hide-details
                        clearable
                        outlined
                        dense
                        @click="getYears"
                    />
                    <v-select
                        v-model="month"
                        class="parts-history-filter"
                        label="Month"
                        :items="months"
                        item-text="text"
                        item-value="value"
                        dark
                        hide-details
                        clearable
                        outlined
                        dense
                    />
                    <v-text-field
                        id="data-text-field"
                        v-model="search"
                        class="parts-history-filter parts-history-filter--search"
                        :append-icon="mdiMagnify"
                        label="Search by order, customer, or address"
                        single-line
                        dense
                        hide-details
                        clearable
                        outlined
                        dark
                    />
                </section>

                <div class="parts-history-table-wrap">
                    <v-data-table
                        class="parts-history-table"
                        dark
                        :server-items-length="serverItemsLength"
                        :loading="serverItemsLoading"
                        :headers="headers"
                        :items="displayedTableItems"
                        :search="search"
                        :year="year"
                        :month="month"
                        :options.sync="options"
                        :footer-props="footerOptions"
                        no-data-text="No parts orders match these filters."
                        no-results-text="No parts orders match these filters."
                        item-key="rmaBatchId"
                        @update:options="lookForMoreServerItems"
                    >
                        <template v-slot:[`item.uid`]="{ item }">
                            <button
                                type="button"
                                class="parts-history-table__open"
                                @click="getBatchOrder(item.rmaBatchId)"
                            >
                                {{ item.rmaBatchId }}
                            </button>
                        </template>
                        <template v-slot:[`item.createdAt`]="{ item }">
                            {{ formatOrderDate(item.createdAt) }}
                        </template>
                        <template v-slot:[`item.address`]="{ item }">
                            {{ orderAddress(item) }}
                        </template>
                    </v-data-table>
                </div>
            </section>

            <parts-order-details-dialog
                v-if="dialog && selectedOrder.length"
                :value="dialog"
                :part-order="selectedOrder"
                @input="dialog = $event"
                @close="dialog = false"
            />
            <v-snackbar v-model="snackbar" right>
                {{ snackbarMessage }}
                <template v-slot:action="{ attrs }">
                    <v-btn text v-bind="attrs" color="white" @click="snackbar = false">Close</v-btn>
                </template>
            </v-snackbar>
        </v-container>
    </main>
</template>

<script>
import PartsOrderDetailsDialog from '@/views/account_dashboard/components/parts_order_details_dialog.vue'
import AccountSettingsHeader from '../account_settings_header.vue'
import { navLinks } from '../navigation.js'
import gql from 'graphql-tag'
import { relayPagination } from '@/mixins/relay_pagination.js'
import Vue from 'vue'
import { mdiMagnify, mdiPackageVariantClosed } from '@mdi/js'
import DownloadFileXlsx from '../../../components/button/download_file_xlsx.vue'

export default Vue.extend({
    metaInfo: { title: `Your Account`, subTitle: `Account Settings` },
    name: 'PartsOrdersHistory',
    components: { PartsOrderDetailsDialog, AccountSettingsHeader, DownloadFileXlsx },
    mixins: [relayPagination('partsOrderBatchConnection')],
    props: {},
    apollo: {
        partsOrderBatchConnection: {
            query: gql`
                query partsOrderBatchConnection(
                    $unHealthy: Boolean
                    $healthy: Boolean
                    $customerOnly: Boolean
                    $after: String
                    $before: String
                    $last: Int
                    $sort: SMSortQuery
                    $first: Int
                    $search: String
                    $year: Int
                    $month: Int
                    $download: Boolean
                ) {
                    partsOrderBatchConnection(
                        unHealthy: $unHealthy
                        healthy: $healthy
                        customerOnly: $customerOnly
                        after: $after
                        before: $before
                        last: $last
                        sort: $sort
                        first: $first
                        search: $search
                        year: $year
                        month: $month
                        download: $download
                    ) {
                        pageInfo {
                            endCursor
                            hasNextPage
                            hasPreviousPage
                            startCursor
                        }
                        edges {
                            cursor
                            node {
                                createdAt
                                rmaBatchId
                                totalQty
                                customer {
                                    billing {
                                        address {
                                            APO
                                            company
                                            line1
                                            line2
                                            city
                                            state
                                            country
                                            postalCode
                                            international
                                        }
                                        contact {
                                            firstName
                                            lastName
                                            fullName
                                            email
                                            phone
                                            company
                                            position
                                        }
                                    }
                                    shipping {
                                        address {
                                            APO
                                            company
                                            line1
                                            line2
                                            city
                                            state
                                            country
                                            postalCode
                                            international
                                        }
                                        contact {
                                            firstName
                                            lastName
                                            fullName
                                            email
                                            phone
                                            company
                                            position
                                        }
                                    }
                                }
                                tpm
                            }
                        }
                        totalCount
                        earliestDate
                    }
                }
            `,
            debounce: 200,
            variables() {
                return {
                    first: this.options.itemsPerPage,
                    search: (this.search || ``).trim(),
                    sort: {
                        key: this.options.sortBy[0],
                        descending: this.options.sortDesc[0]
                    },
                    customerOnly: true,
                    month: this.month,
                    year: this.year,
                    download: this.download
                }
            }
        }
    },
    data() {
        return {
            mdiMagnify,
            mdiPackageVariantClosed,
            snackbar: false,
            panel: 0,
            dialog: false,
            search: '',
            snackbarMessage: '',
            selectedOrder: [],
            selectedBatch: [],
            groupedOrders: [],
            links: navLinks,
            customerOrders: [],
            download: false,
            earliestDate: null,
            years: [],
            year: null,
            month: null,
            months: Array.from({ length: 12 }, (_, i) => ({
                text: new Date(0, i).toLocaleString('en-US', { month: 'short' }), 
                value: i + 1                                                      
            })),
            footerOptions: {
                itemsPerPageOptions: [10, 25, 50]
            },
            options: {
                sortBy: ['createdAt'],
                sortDesc: [true],
                itemsPerPage: 10,
                page: 1
            },
            btn: 'Download Parts Order History',
            downloadTableData: [],
            downloadTableHeader: [
                {text: 'Parts Order', value: 'uid'},
                {text: 'Customer Name', value: 'customerName'},
                {text: 'Address', value: 'address'},
                {text: 'City', value: 'city'},
                {text:'State', value: 'state'},
                {text: 'Postal Code', value: 'postalCode'},
                {text: 'Create Date', value: 'createdAt'},
                {text: '# of Items', value: 'totalQty'}
                ],
            filename: 'PartsOrderHistory.xlsx',
            headers: [
                {
                    text: 'Parts Order',
                    value: 'uid',
                    align: 'start',
                    sortable: false
                },
                {
                    text: 'Customer Name',
                    value: 'customer.shipping.contact.fullName',
                    sortable: false
                },
                {
                    text: 'Address',
                    value: `address`,
                    sortable: false
                },
                {
                    text: 'Create Date',
                    value: 'createdAt',
                    sortable: false
                },
                {
                    text: '# of Items',
                    align: 'start',
                    value: 'totalQty',
                    sortable: false
                }
            ],
            fetchedData: {}
        }
    },
    created() {},
    watch: {
        year: {
            handler() {
                this.refetchPartsOrder()
            }
        },
        month: {
            handler() {
                this.refetchPartsOrder()
            }
        }
    },
    methods: {
        formatOrderDate(value) {
            if (!value) return '—'
            const date = new Date(value)
            return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
        },
        orderAddress(item) {
            const address = item.customer?.shipping?.address || {}
            return [address.line1, address.city, address.state, address.country, address.postalCode]
                .filter(Boolean)
                .join(', ') || '—'
        },
        refetchPartsOrder(){
            this.$apollo.queries.partsOrderBatchConnection.fetchMore({
                    variables: {
                        first: this.options.itemsPerPage,
                        search: (this.search || ``).trim(),
                        healthy: this.showHealthy,
                        unHealthy: this.showUnHealthy,
                        sort: {
                            key: this.options.sortBy[0],
                            descending: this.options.sortDesc[0]
                        },
                        customerOnly: true,
                        year: this.year,
                        month:this.month,
                        download: this.download
                    }
                })
        },
        getYears() {
            if (this.years.length <= 0) {
                this.earliestDate = new Date(this.$apollo.data.partsOrderBatchConnection.earliestDate) 
                
                if (!this.earliestDate) {
                    this.earliestDate = new Date()
                }

                const result = []
                const startYear = this.earliestDate.getFullYear()
                const endDate = new Date()
                const endYear = endDate.getFullYear()

                for (let i = startYear; i <= endYear; i++) {
                    result.unshift(i)
                }
                this.years = result
            }
        },
        async downloadData(){
            this.download = true
            this.options.itemsPerPage = 0
            await this.$apollo.queries.partsOrderBatchConnection.fetchMore({
                    variables: {
                        first: this.options.itemsPerPage,
                        search: (this.search || ``).trim(),
                        healthy: this.showHealthy,
                        unHealthy: this.showUnHealthy,
                        sort: {
                            key: this.options.sortBy[0],
                            descending: this.options.sortDesc[0]
                        },
                        customerOnly: true,
                        year: this.year,
                        month:this.month,
                        download: this.download
                    }
                }).then((data) => {
                    this.fetchedData = data.data.partsOrderBatchConnection || []
                })

            const data = this.fetchedData
            const items = data.edges.map((e) => e.node)
            const rows = items.map(({rmaBatchId, customer , createdAt, totalQty}) => ({
                'uid': rmaBatchId ,
                'createdAt': createdAt ,
                "customerName": customer.shipping?.contact?.fullName ,
                "address": customer.shipping?.address?.line1,
                "city": customer.shipping?.address?.city,
                "state": customer.shipping?.address?.country,
                "postalCode": customer.shipping?.address?.postalCode ,
                "totalQty": totalQty 
            }))
            
            this.downloadTableData = rows
            
            this.download = false
            this.options.itemsPerPage = 10
        },
        async getBatchOrder(batchId) {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetPartsOrderDetail($orderBatchId: String!) {
                            getPartsOrderDetail(orderBatchId: $orderBatchId) {
                                version
                                uid
                                userId
                                metadata {
                                    health {
                                        atCreateMoveAttachments {
                                            applied
                                            error
                                        }
                                        atCreateECN {
                                            applied
                                            error
                                        }
                                        atCreateShipping {
                                            applied
                                            error
                                        }
                                        atCreateRSPL {
                                            applied
                                            error
                                        }
                                        atCreateTPM {
                                            applied
                                            error
                                        }
                                        atCreateSendConfEmail {
                                            applied
                                            error
                                        }
                                    }
                                    bouncer
                                    doa
                                    reportedIncorrectly
                                    rulesApplied {
                                        name
                                        index
                                    }
                                    overrides {
                                        tpm
                                        targetUser
                                        skipEmail
                                        shippingMethod
                                        explanation
                                        overriddenBy
                                    }
                                }
                                stage {
                                    created {
                                        postedAt
                                        postedBy
                                        tpmCreatedAt
                                        appliedPn
                                        appliedQty
                                    }
                                    reported {
                                        postedAt
                                        postedBy
                                        tpmCreatedAt
                                    }
                                    closed {
                                        postedAt
                                        postedBy
                                        tpmCreatedAt
                                    }
                                    shipped {
                                        postedAt
                                        postedBy
                                        tpmCreatedAt
                                        shippedPn
                                        shippedQty
                                    }
                                    cancelled {
                                        postedAt
                                        postedBy
                                        tpmCreatedAt
                                    }
                                }
                                createdAt
                                closedAt
                                tracking {
                                    inbound {
                                        uid
                                        service
                                        created
                                        carrier
                                        labelUrl
                                        trackingNumber
                                        trackingUrl
                                        commercialInvoiceUrl
                                        sent
                                        received
                                        eta
                                        reference
                                    }
                                    outbound {
                                        uid
                                        service
                                        created
                                        carrier
                                        labelUrl
                                        trackingNumber
                                        trackingUrl
                                        commercialInvoiceUrl
                                        sent
                                        received
                                        eta
                                        reference
                                    }
                                    box {
                                        uid
                                        service
                                        created
                                        carrier
                                        labelUrl
                                        trackingNumber
                                        trackingUrl
                                        commercialInvoiceUrl
                                        sent
                                        received
                                        eta
                                        reference
                                    }
                                }
                                rmaBatchId
                                rmaType
                                unit {
                                    serial
                                    warrantyType
                                    endDeviceWarranty
                                    endBatteryWarranty
                                    endAccessoryWarranty
                                    shipDate
                                    model
                                    mfgModel
                                    deviceSpecs {
                                        biosVersion
                                        fullDescription
                                        hdiVersion
                                        hdiPart
                                        imei
                                        description
                                        pn
                                        mfgPn
                                        motherboardPn
                                        motherboardSerial
                                    }
                                    sla {
                                        tat
                                        shippingMethod
                                        keepHd
                                        zeroDefect
                                        lcdUpgrade
                                        saltFog
                                        cosmeticUpgrade
                                        fodUpgrade
                                        emi
                                    }
                                    maxSMQty
                                }
                                smId
                                category
                                partSpec
                                status
                                closeReason
                                partDescription
                                tpm
                                permissions {
                                    reimage
                                    biosFirmware
                                    userId
                                    userPassword
                                    biosPassword
                                }
                                customer {
                                    shipping {
                                        address {
                                            APO
                                            company
                                            line1
                                            line2
                                            city
                                            state
                                            country
                                            postalCode
                                            international
                                        }
                                        contact {
                                            firstName
                                            lastName
                                            fullName
                                            email
                                            phone
                                            company
                                            position
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        orderBatchId: batchId
                    }
                })
                const partOrder = data?.getPartsOrderDetail
                if (!Array.isArray(partOrder) || !partOrder.length) {
                    throw new Error('Parts order details unavailable')
                }
                this.selectedOrder = partOrder.map((order) => ({
                    ...order,
                    customer: {
                        ...order.customer,
                        shipping: {
                            ...order.customer?.shipping,
                            address: order.customer?.shipping?.address || {},
                            contact: order.customer?.shipping?.contact || {}
                        }
                    },
                    tracking: order.tracking || {}
                }))
                this.dialog = true
            } catch (error) {
                this.snackbarMessage = 'We could not open this order. Please try again.'
                this.snackbar = true
            }
        }
    }
})
</script>

<style scoped>
.parts-history-page {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    padding: 34px 0 54px;
    color: #f2f4fa;
    background: radial-gradient(ellipse at 88% 0, rgba(219, 81, 32, 0.15), transparent 38rem),
        linear-gradient(145deg, #292c35, #22252d 65%, #2a2d35);
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.parts-history-page::before {
    position: fixed;
    z-index: 0;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    content: '';
    filter: grayscale(1) brightness(0.8);
    mix-blend-mode: soft-light;
    opacity: 0.1;
    pointer-events: none;
}
.parts-history-page__container {
    position: relative;
    z-index: 1;
    max-width: 1260px;
}
.parts-history-page__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin: 0 0 20px;
    padding: 3px 2px 0;
}
.parts-history-page__heading-copy {
    position: relative;
    padding-left: 17px;
}
.parts-history-page__heading-copy::before {
    position: absolute;
    top: 2px;
    bottom: 3px;
    left: 0;
    width: 3px;
    background: #db5120;
    border-radius: 3px;
    content: '';
}
.parts-history-page__eyebrow {
    color: #ff9a74;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.4px;
}
.parts-history-page__heading h2 {
    margin: 5px 0 4px;
    color: #f4f5f8;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 600;
    letter-spacing: -0.75px;
}
.parts-history-page__heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 12px;
    line-height: 1.5;
}
.parts-history-page__heading-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
}
.parts-history-page__count {
    display: inline-flex;
    min-height: 34px;
    align-items: center;
    padding: 0 10px;
    color: #c7cbd4;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
}
.parts-history-page__download {
    flex: 0 0 auto;
}
.parts-history-card {
    overflow: hidden;
    color: #f1f2f5;
    background: rgba(35, 38, 46, 0.88);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
}
.parts-history-filters {
    display: grid;
    grid-template-columns: minmax(130px, 0.7fr) minmax(140px, 0.8fr) minmax(240px, 1.5fr);
    align-items: center;
    gap: 12px;
    padding: 14px 20px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}
.parts-history-filter {
    min-width: 0;
    margin: 0;
}
.parts-history-filter ::v-deep .v-input__slot {
    min-height: 40px;
    padding: 0 11px;
    background: rgba(18, 20, 26, 0.36);
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 6px;
}
.parts-history-filter ::v-deep .v-input__slot::before,
.parts-history-filter ::v-deep .v-input__slot::after {
    display: none;
}
.parts-history-filter ::v-deep .v-label,
.parts-history-filter ::v-deep .v-icon {
    color: #aeb5c3;
    font-size: 11px;
}
.parts-history-filter ::v-deep input,
.parts-history-filter ::v-deep .v-select__selection {
    color: #f1f2f5;
    font-size: 11px;
}
.parts-history-table-wrap {
    overflow: hidden;
    padding: 0 12px 12px;
}
.parts-history-table {
    background: transparent !important;
    font-family: 'Inter Tight', 'Roboto', sans-serif !important;
}
.parts-history-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
    background: #22252d;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.parts-history-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}
.parts-history-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.parts-history-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.parts-history-table ::v-deep table {
    min-width: 1040px;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    color: #e9edf5;
    font-family: 'Inter Tight', 'Roboto', sans-serif !important;
}
.parts-history-table ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    height: 46px;
    padding: 0 14px;
    color: #d6d9e1 !important;
    background: #30333b;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05px;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    text-align: left !important;
    white-space: nowrap;
}
.parts-history-table ::v-deep thead th + th {
    box-shadow: inset 1px 0 rgba(255, 255, 255, 0.045);
}
.parts-history-table ::v-deep td {
    height: 52px;
    padding: 10px 14px;
    overflow: hidden;
    color: #e2e5ec !important;
    background: #282b33;
    border-bottom: 1px solid rgba(255, 255, 255, 0.055) !important;
    font-size: 11px;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-weight: 400;
    line-height: 1.5;
    text-align: left !important;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.parts-history-table ::v-deep tbody td + td {
    box-shadow: inset 1px 0 rgba(255, 255, 255, 0.025);
}
.parts-history-table ::v-deep tbody tr:nth-child(even) td {
    background: #252830;
}
.parts-history-table ::v-deep tbody tr:hover td {
    background: rgba(219, 81, 32, 0.12) !important;
}
.parts-history-table ::v-deep .v-data-footer {
    color: #aeb5c3;
    background: transparent;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 10px;
}
.parts-history-table ::v-deep .v-data-footer .v-icon {
    color: #c6cbd5;
}
.parts-history-table__open {
    max-width: 100%;
    min-height: 28px;
    overflow: hidden;
    padding: 2px 3px;
    color: #ffb396;
    background: none;
    border: 0;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.1px;
    line-height: 1.5;
    text-overflow: ellipsis;
    cursor: pointer;
}
.parts-history-table__open:hover {
    text-decoration: underline;
}
.parts-history-table__open:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 760px) {
    .parts-history-filters {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .parts-history-filter--search {
        grid-column: 1 / -1;
    }
}
@media (max-width: 520px) {
    .parts-history-page {
        padding-top: 20px;
    }
    .parts-history-page__heading {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 15px;
    }
    .parts-history-page__heading-actions {
        width: 100%;
        justify-content: space-between;
    }
    .parts-history-filters {
        grid-template-columns: minmax(0, 1fr);
        padding: 13px 15px;
    }
    .parts-history-filter--search {
        grid-column: auto;
    }
}
</style>

<style scoped>
.parts-history-page {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    padding: 34px 0 54px;
    color: #f2f4fa;
    background: radial-gradient(ellipse at 88% 0, rgba(219, 81, 32, 0.15), transparent 38rem),
        linear-gradient(145deg, #292c35, #22252d 65%, #2a2d35);
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.parts-history-page::before {
    position: fixed;
    z-index: 0;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    content: '';
    filter: grayscale(1) brightness(0.8);
    mix-blend-mode: soft-light;
    opacity: 0.1;
    pointer-events: none;
}
.parts-history-page__container {
    position: relative;
    z-index: 1;
    max-width: 1260px;
}
.parts-history-card {
    overflow: hidden;
    color: #f1f2f5;
    background: rgba(35, 38, 46, 0.88);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
}
.parts-history-card__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 20px 23px 16px;
    background: radial-gradient(ellipse at 0 0, rgba(219, 81, 32, 0.11), transparent 65%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}
.parts-history-card__eyebrow {
    color: #f2a58b;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1.3px;
}
.parts-history-card__heading h2 {
    margin: 4px 0 3px;
    color: #f4f5f8;
    font-size: 18px;
    font-weight: 600;
}
.parts-history-card__heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 11px;
}
.parts-history-card__download {
    flex: 0 0 auto;
}
.parts-history-filters {
    display: grid;
    grid-template-columns: minmax(130px, 0.7fr) minmax(140px, 0.8fr) minmax(240px, 1.5fr);
    align-items: center;
    gap: 12px;
    padding: 14px 20px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}
.parts-history-filter {
    min-width: 0;
    margin: 0;
}
.parts-history-filter ::v-deep .v-input__slot {
    min-height: 40px;
    padding: 0 11px;
    background: rgba(18, 20, 26, 0.36);
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 6px;
}
.parts-history-filter ::v-deep .v-input__slot::before,
.parts-history-filter ::v-deep .v-input__slot::after {
    display: none;
}
.parts-history-filter ::v-deep .v-label,
.parts-history-filter ::v-deep .v-icon {
    color: #aeb5c3;
    font-size: 11px;
}
.parts-history-filter ::v-deep input,
.parts-history-filter ::v-deep .v-select__selection {
    color: #f1f2f5;
    font-size: 11px;
}
.parts-history-table-wrap {
    overflow: hidden;
    padding: 0 12px 12px;
}
.parts-history-table {
    background: transparent !important;
    font-family: 'Inter Tight', 'Roboto', sans-serif !important;
}
.parts-history-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
    background: #22252d;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.parts-history-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}
.parts-history-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #20222a;
    border-radius: 9px;
}
.parts-history-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.parts-history-table ::v-deep table {
    min-width: 1040px;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    color: #e9edf5;
    font-family: 'Inter Tight', 'Roboto', sans-serif !important;
}
.parts-history-table ::v-deep th {
    height: 46px;
    padding: 0 14px;
    color: #d6d9e1 !important;
    background: #30333b;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
    font-size: 11px;
    font-weight: 600;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    letter-spacing: 0.05px;
    text-align: left !important;
    text-transform: none;
    white-space: nowrap;
}
.parts-history-table ::v-deep td {
    height: 52px;
    padding: 10px 14px;
    overflow: hidden;
    color: #e2e5ec !important;
    background: #282b33;
    border-bottom: 1px solid rgba(255, 255, 255, 0.055) !important;
    font-size: 11px;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-weight: 400;
    line-height: 1.5;
    text-align: left !important;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.parts-history-table ::v-deep tbody tr:nth-child(even) td {
    background: #252830;
}
.parts-history-table ::v-deep tbody tr:hover td {
    background: rgba(219, 81, 32, 0.12) !important;
}
.parts-history-table ::v-deep .v-data-footer {
    color: #aeb5c3;
    background: transparent;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 10px;
}
.parts-history-table__open {
    max-width: 100%;
    min-height: 28px;
    overflow: hidden;
    padding: 2px 3px;
    color: #ffb396;
    background: none;
    border: 0;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.1px;
    line-height: 1.5;
    text-overflow: ellipsis;
    cursor: pointer;
}
.parts-history-table__open:hover {
    text-decoration: underline;
}
.parts-history-table__open:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 760px) {
    .parts-history-filters {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .parts-history-filter--search {
        grid-column: 1 / -1;
    }
}
@media (max-width: 520px) {
    .parts-history-page {
        padding-top: 20px;
    }
    .parts-history-card__heading {
        align-items: flex-start;
        flex-direction: column;
        padding: 18px 16px 15px;
    }
    .parts-history-filters {
        grid-template-columns: minmax(0, 1fr);
        padding: 13px 15px;
    }
    .parts-history-filter--search {
        grid-column: auto;
    }
}
</style>
